<template>
  <div class="institutions-search__input">
    <UIInputTextClearable
      v-model="institutionSearch"
      placeholder="Find institution..."
    >
      <template #leading>
        <MagnifyingGlassIcon aria-hidden="true" />
      </template>
    </UIInputTextClearable>
  </div>
  <UIPreloader
    v-if="institutionsLoading"
    message="Loading..."
    class="institutions-search__loading"
  />
  <UILayoutNoData
    v-else-if="debouncedInstitutionSearch && !instatutionsList.length"
    class="institutions-search__no-data"
    inline
    hide-question-icon
  />
  <div v-else class="institutions-search__list">
    <InstitutionsSearchItem
      v-for="(item, index) in instatutionsList"
      :key="item.institution_id"
      v-bind="{ item }"
      :recommended="isItemRecommended(index)"
      @click="handleClickInstitution(item)"
    />
    <div v-if="loading" class="institutions-search__loading-overlay">
      <UIPreloader message="Please wait..." />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { debouncedRef } from '@vueuse/core'

import { handleCatchedError } from '@/helpers/common'

import { InstatutionData } from '../../utils/types'
import { DEBOUNCE_DELAY } from '@/const'
import { DEFAULT_INSTITUTIONS } from '../../utils/const'

import { useLinkedDataConnectorsStore } from '@/store/linkedData/connectors'

import { UILayoutNoData, UIPreloader } from '@ui/core'
import { UIInputTextClearable } from '@ui/fields'
import InstitutionsSearchItem from './InstitutionsSearchItem.vue'
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'

defineExpose({
  clearSearch() {
    institutionSearch.value = ''
  },
})

type Props = {
  loading?: boolean
}

type Emits = {
  'click-institution': [data: InstatutionData]
}

const { loading } = defineProps<Props>()
const emit = defineEmits<Emits>()

const linkedDataConnectorsStore = useLinkedDataConnectorsStore()

const institutionSearch = ref('')
const debouncedInstitutionSearch = debouncedRef(
  institutionSearch,
  DEBOUNCE_DELAY,
)
const institutionsLoading = ref(false)
const instatutionsList = ref<InstatutionData[]>(DEFAULT_INSTITUTIONS)

const controller = ref<AbortController>()

const isItemRecommended = (index: number) =>
  !!debouncedInstitutionSearch.value && index === 0

const handleClickInstitution = async (item: InstatutionData) => {
  emit('click-institution', item)
}

watch(debouncedInstitutionSearch, async value => {
  controller.value?.abort()
  if (!value) {
    instatutionsList.value = DEFAULT_INSTITUTIONS
    return
  }
  institutionsLoading.value = true
  try {
    controller.value = new AbortController()
    instatutionsList.value = await linkedDataConnectorsStore.searchInstitutions(
      value,
      controller.value?.signal,
    )
  } catch (e) {
    handleCatchedError(e as string)
  } finally {
    institutionsLoading.value = false
  }
})
</script>

<style scoped>
.institutions-search {
  &__loading,
  &__no-data {
    @apply min-h-[114px];
    @apply mt-4;
  }

  &__list {
    @apply grid grid-cols-1 gap-2;
  }

  &__loading-overlay {
    @apply absolute inset-0 flex items-center justify-center;
    @apply bg-white/50 dark:bg-gray-900/50;
  }
}
</style>
